/**
 * Premium Financial Needs Analysis Report Generator
 * Creates comprehensive, branded PDF reports with charts, insights, and professional formatting
 */

import { db } from '../firebase';
import { COLLECTIONS } from './firestore-schema';

/**
 * Generate AI-powered insights and explanations for financial data
 */
export function generateInsights(reportData, calculations) {
  const insights = {
    cashFlow: '',
    survivor: '',
    disability: '',
    ltc: '',
    emergencyFund: '',
    debtManagement: '',
    retirement: '',
    education: '',
    overall: '',
  };

  const client1Name = reportData.client1?.name || 'Client 1';
  const client2Name = reportData.client2?.name || '';
  const householdIncome = (reportData.client1?.salary || 0) + (reportData.client2?.salary || 0);
  const monthlyIncome = householdIncome / 12;

  // Cash Flow Insights
  const discretionaryIncome = calculations.cashFlow?.discretionaryIncome || 0;
  if (discretionaryIncome > 0) {
    insights.cashFlow = `Excellent! You have $${discretionaryIncome.toLocaleString()} in discretionary income each month. This provides flexibility to accelerate your financial goals. Consider allocating a portion to emergency savings, debt reduction, or retirement contributions to maximize your financial growth.`;
  } else {
    insights.cashFlow = `Your current expenses exceed your income by $${Math.abs(discretionaryIncome).toLocaleString()} monthly. This is a critical situation that requires immediate attention. Focus on reducing expenses, increasing income, or both. Every dollar saved today compounds into significant wealth tomorrow.`;
  }

  // Survivor Needs Insights
  const survivor1Need = calculations.survivor1?.remainingNeed || 0;
  const survivor2Need = calculations.survivor2?.remainingNeed || 0;
  if (survivor1Need > 0 || survivor2Need > 0) {
    insights.survivor = `Life insurance is not just about death benefits—it's about protecting the dreams you've built together. ${survivor1Need > 0 ? `For ${client1Name}, a gap of $${survivor1Need.toLocaleString()} means your family's financial security is at risk.` : ''} ${survivor2Need > 0 ? `For ${client2Name || 'your spouse'}, $${survivor2Need.toLocaleString()} in additional coverage ensures your children's education, mortgage payments, and lifestyle can continue uninterrupted.` : ''} This protection allows your family to grieve without financial stress, ensuring your legacy lives on through their continued success.`;
  } else {
    insights.survivor = `Congratulations! Your life insurance coverage appears adequate. However, life circumstances change—review your coverage annually, especially after major life events like births, home purchases, or career changes.`;
  }

  // Disability Insights
  const disability1Need = calculations.disability1?.monthlyRemaining || 0;
  const disability2Need = calculations.disability2?.monthlyRemaining || 0;
  if (disability1Need > 0 || disability2Need > 0) {
    insights.disability = `Disability is more likely than death before age 65. ${disability1Need > 0 ? `${client1Name} needs $${disability1Need.toLocaleString()}/month in disability coverage.` : ''} ${disability2Need > 0 ? `${client2Name || 'Your spouse'} needs $${disability2Need.toLocaleString()}/month.` : ''} Without this protection, a disability could force you to drain retirement savings, derailing decades of planning. Disability insurance protects not just your income, but your future.`;
  }

  // LTC Insights (use lump sum for 3-year cost; fallback to remainingMonthlyNeed * 36)
  const ltc1Lump = calculations.ltc1?.lumpSumAtLTC ?? (calculations.ltc1?.remainingMonthlyNeed ?? calculations.ltc1?.remainingNeed ?? 0) * 36;
  const ltc2Lump = calculations.ltc2?.lumpSumAtLTC ?? (calculations.ltc2?.remainingMonthlyNeed ?? calculations.ltc2?.remainingNeed ?? 0) * 36;
  const ltcLumpSum = Math.max(ltc1Lump, ltc2Lump);
  if (ltcLumpSum > 0) {
    insights.ltc = `70% of people over 65 will need long-term care. A 3-year stay could cost over $${ltcLumpSum.toLocaleString()} in today's dollars. Without LTC insurance, this expense could devastate your retirement savings, leaving little for your spouse or heirs. Planning now, while you're healthy and premiums are lower, protects your retirement and preserves your estate.`;
  }

  // Emergency Fund Insights
  const emergencyProgress = calculations.emergencyFund?.remainingNeed || 0;
  const emergencyTotal = calculations.emergencyFund?.totalNeed || 0;
  const emergencyHave = calculations.emergencyFund?.currentEmergencyFund || 0;
  const progressPercent = emergencyTotal > 0 ? ((emergencyHave / emergencyTotal) * 100).toFixed(0) : 0;
  
  if (emergencyProgress > 0) {
    insights.emergencyFund = `You're ${progressPercent}% of the way to your emergency fund goal. The remaining $${emergencyProgress.toLocaleString()} provides crucial peace of mind. An emergency fund is your financial safety net—it prevents you from going into debt during unexpected events and gives you the confidence to make bold financial decisions.`;
  } else {
    insights.emergencyFund = `Outstanding! Your emergency fund is fully funded. This financial cushion allows you to take calculated risks, pursue opportunities, and sleep well knowing you're prepared for life's surprises.`;
  }

  // Debt Management Insights
  const debtRatio = calculations.debtManagement?.debtToIncomeRatio || 0;
  if (debtRatio > 20) {
    insights.debtManagement = `Your debt-to-income ratio of ${debtRatio.toFixed(1)}% is above the recommended 20%. High debt limits your financial flexibility and can impact your credit score. Focus on paying off high-interest debt first, then tackle lower-rate debt. Every dollar paid toward debt is an investment in your financial freedom.`;
  } else if (debtRatio > 0) {
    insights.debtManagement = `Your debt-to-income ratio of ${debtRatio.toFixed(1)}% is manageable. Continue making consistent payments and avoid taking on new debt. Consider accelerating payments on high-interest debt to free up cash flow faster.`;
  } else {
    insights.debtManagement = `Excellent! You have minimal debt relative to your income. This strong financial position allows you to focus on wealth-building strategies like investing and retirement planning.`;
  }

  // Retirement Insights
  const retirementShortfall = calculations.retirement?.shortfall || 0;
  const monthlySavingsNeeded = calculations.retirement?.monthlySavingsNeeded || 0;
  if (retirementShortfall > 0) {
    insights.retirement = `To maintain your desired lifestyle in retirement, you'll need to save an additional $${monthlySavingsNeeded.toLocaleString()} per month. This may seem daunting, but remember: starting today means less to save each month due to compound interest. Every year you delay increases the monthly amount needed by approximately 10-15%. The best time to start was yesterday; the second-best time is today.`;
  } else {
    insights.retirement = `Congratulations! Your retirement projections show you're on track. Continue your current savings strategy and consider increasing contributions as your income grows. Small increases now can significantly enhance your retirement lifestyle.`;
  }

  // Education Insights
  const educationShortfall = calculations.education?.shortfall || 0;
  const educationMonthly = calculations.education?.monthlySavingsNeeded || 0;
  if (educationShortfall > 0) {
    insights.education = `Education costs are rising faster than inflation—approximately 6% annually. To fully fund your children's education, you'll need to save $${educationMonthly.toLocaleString()} per month. Starting early leverages the power of compounding. A 529 plan offers tax advantages and can significantly reduce the amount you need to save. Remember: student loans can burden graduates for decades—your savings today give them a head start in life.`;
  } else {
    insights.education = `Great planning! Your education savings are on track. Continue contributing consistently and consider increasing contributions if your income allows.`;
  }

  // Overall Insights
  const totalGaps = [
    survivor1Need > 0 ? survivor1Need : 0,
    survivor2Need > 0 ? survivor2Need : 0,
    (disability1Need + disability2Need) * 12,
    ltc1Lump + ltc2Lump,
    emergencyProgress,
    retirementShortfall,
    educationShortfall,
  ].reduce((sum, val) => sum + val, 0);

  if (totalGaps > 0) {
    insights.overall = `Your comprehensive financial analysis reveals opportunities to strengthen your financial foundation. While you've made progress in several areas, addressing the identified gaps will provide comprehensive protection and accelerate your path to financial independence. Remember: financial planning is a journey, not a destination. Small, consistent actions today create extraordinary results tomorrow.`;
  } else {
    insights.overall = `Outstanding financial planning! You've built a strong foundation across all areas. Continue monitoring your progress, adjusting as life changes, and maintaining discipline. You're well-positioned to achieve your financial dreams.`;
  }

  return insights;
}

/**
 * Generate chart data for visualizations
 */
export function generateChartData(calculations, reportData) {
  const charts = {
    needsBreakdown: null,
    retirementProjection: null,
    educationTimeline: null,
    cashFlowBreakdown: null,
  };

  // Needs Breakdown Chart (Pie/Bar)
  const needs = [
    { name: 'Survivor Protection', value: (calculations.survivor1?.remainingNeed || 0) + (calculations.survivor2?.remainingNeed || 0) },
    { name: 'Disability Protection', value: ((calculations.disability1?.monthlyRemaining || 0) + (calculations.disability2?.monthlyRemaining || 0)) * 12 },
    { name: 'Long-Term Care', value: (calculations.ltc1?.remainingNeed || 0) + (calculations.ltc2?.remainingNeed || 0) },
    { name: 'Emergency Fund', value: calculations.emergencyFund?.remainingNeed || 0 },
    { name: 'Retirement Shortfall', value: calculations.retirement?.shortfall || 0 },
    { name: 'Education Shortfall', value: calculations.education?.shortfall || 0 },
  ].filter(n => n.value > 0);

  charts.needsBreakdown = needs;

  // Retirement Projection
  const currentAge = Math.min(
    reportData.client1?.age || 40,
    reportData.client2?.age || 40
  );
  const retirementAge = parseInt(reportData.retirementAge || 65);
  const yearsToRetirement = retirementAge - currentAge;
  
  if (yearsToRetirement > 0) {
    const currentAssets = calculations.retirement?.projectedAssets || 0;
    const needed = calculations.retirement?.lumpSumNeeded || 0;
    const monthlySavings = calculations.retirement?.monthlySavingsNeeded || 0;
    
    charts.retirementProjection = {
      currentAge,
      retirementAge,
      yearsToRetirement,
      currentAssets,
      needed,
      monthlySavings,
      projection: generateRetirementProjection(currentAssets, monthlySavings, yearsToRetirement, 0.08),
    };
  }

  // Education Timeline
  if (reportData.children && reportData.children.length > 0) {
    charts.educationTimeline = reportData.children.map(child => {
      const childAge = child.age || 0;
      const yearsUntilCollege = 18 - childAge;
      const cost = calculations.education?.totalLumpSumNeeded || 0;
      return {
        name: child.name,
        currentAge: childAge,
        yearsUntilCollege,
        estimatedCost: cost / (reportData.children.length || 1),
      };
    });
  }

  // Cash Flow Breakdown
  charts.cashFlowBreakdown = {
    income: (calculations.cashFlow?.monthlySalary || 0),
    taxes: (calculations.cashFlow?.monthlyTaxes || 0),
    debtPayments: (calculations.cashFlow?.monthlyDebtPayments || 0),
    savings: (calculations.cashFlow?.monthlySavings || 0),
    insurance: (calculations.cashFlow?.lifeInsurancePremiums || 0),
    discretionary: (calculations.cashFlow?.discretionaryIncome || 0),
  };

  return charts;
}

/**
 * Generate retirement projection data points
 */
function generateRetirementProjection(currentAssets, monthlySavings, years, growthRate) {
  const projection = [];
  let balance = currentAssets;
  
  for (let year = 0; year <= years; year++) {
    projection.push({
      age: year,
      balance: balance,
    });
    balance = balance * (1 + growthRate) + (monthlySavings * 12);
  }
  
  return projection;
}

/**
 * Save generated PDF report to database
 */
export async function savePdfReport({ userId, userEmail, toolId, reportData, pdfBlob, pdfUrl }) {
  if (!db || !userId) {
    throw new Error('Missing required parameters');
  }

  const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
  const col = collection(db, COLLECTIONS.REPORTS);
  
  // Convert blob to base64 if provided
  let pdfBase64 = null;
  if (pdfBlob) {
    pdfBase64 = await blobToBase64(pdfBlob);
  }
  
  const docRef = await addDoc(col, {
    userId,
    userEmail: (userEmail || '').toLowerCase().trim(),
    toolId,
    reportData: {
      ...reportData,
      reportType: 'premium_pdf',
      pdfGenerated: true,
    },
    pdfBlob: pdfBase64,
    pdfUrl: pdfUrl || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Convert blob to base64 for storage
 */
async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
